/**
 * @lines98/client/components/account-chip — чип залогиненного игрока в account-slot
 * (account-profile-redesign.md §2.2 + §3, P1/P2).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-05-30 «ствол → ветки» + spec §2.2):
 * когда игрок залогинен, account-slot рейла показывает chip — ник + caret; клик по
 * ВСЕЙ строке → меню `Профиль · ——— · Выйти` (§3). Это переиспользуемый примитив
 * (как icon-button / level-badge): рейл монтирует его тонкой веткой, не верстает
 * руками. Меню — лёгкий dropdown СТРОГО под строкой чипа, в ширину рейла (owner-
 * directive 2026-06-12): wrap position:relative → menu absolute top:100% (не модальный
 * rail-popover). Закрытие: выбор / клик вне / Esc. Аватар на рейле УБРАН (owner-
 * directive 2026-06-12) — модуль `lib/avatar` остаётся за шапкой Профиля (§5).
 *
 * SCOPE: фабрика `mountAccountChip(host, opts) → handle`. Ник — must-visible (min-
 * width:0 + ellipsis на узком), caret descriptive. Клик по whole-row открывает меню
 * (§2.2 — не только caret). «Выйти» — деструктив внизу за дивайдером (§3 / research
 * P2). Размеры/токены — внутри (account-chip.css).
 *
 * NOT IN SCOPE: сам logout (`onLogout` инъектируется — ОДНА общая функция, §3.1, не
 * копия), рендер гостя (там пилюля «Войти», не chip — §2.1). Переход в Профиль идёт
 * пунктом nav-list рейла (свой `onSelect`), НЕ через чип (owner-directive 2026-06-12 —
 * в меню чипа только «Выйти»).
 *
 * Cross-references:
 *   - account-profile-redesign.md §2.2 (chip), §3 (меню), §3.1 (Выйти), AC-4/AC-13.
 *   - nav-rail (монтаж в account-slot). Меню-dropdown — внутри этого модуля (один потребитель).
 *   - account-chip.css (co-located; токен-размеры + must-visible/descriptive).
 */

import './account-chip.css';

import { t } from '../lib/i18n/index.js';
import { createPillButton } from './pill-button.js';

export interface AccountChipOptions {
  /** UUID игрока — часть идентичности (аватар на рейле убран, см. §2.2-upd 2026-06-12). */
  readonly playerId: string;
  /** Ник (server-truth displayName) или fallback-лейбл; descriptive в chip. */
  readonly displayName: string;
  /** «Выйти» из меню (§3.1) — ОДНА общая logout-функция (инъектируется, не копия). */
  readonly onLogout: () => void | Promise<void>;
}

export interface AccountChipHandle {
  /** Корневой элемент chip (account-slot host получил его смонтированным). */
  readonly element: HTMLElement;
  /** Снять слушатели/меню (на пере-рендере slot / detach рейла). */
  unmount(): void;
}

/**
 * Смонтировать account-chip в `host`. Возвращает handle. Строка chip = кнопка-триггер
 * меню (whole-row, §2.2): иконка-«вошёл» + ник + caret. Меню — dropdown строго под
 * строкой (в ширину рейла) с ОДНОЙ accent-пилюлей «Выйти» (owner-directive 2026-06-12).
 * Ник must-visible (ellipsis на узком, AC-13).
 */
export function mountAccountChip(host: HTMLElement, opts: AccountChipOptions): AccountChipHandle {
  // Триггер = вся строка chip (кнопка, чтобы клик по любой её части открывал меню §2.2).
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'nav-item account-chip';

  // Ведущая иконка «вошёл» (owner-directive 2026-06-12): аватар убран → нужен явный
  // маркер залогиненного состояния перед ником. Декоративна (aria-hidden).
  const icon = document.createElement('span');
  icon.className = 'account-chip-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = ICON_USER_CHECK;

  const nameEl = document.createElement('span');
  nameEl.className = 'account-chip-name';
  nameEl.textContent = opts.displayName;

  const caret = document.createElement('span');
  caret.className = 'account-chip-caret';
  caret.setAttribute('aria-hidden', 'true');
  caret.innerHTML = ICON_CARET;

  // chip = иконка-«вошёл» + ник + caret (аватар убран, owner-directive 2026-06-12).
  trigger.append(icon, nameEl, caret);
  trigger.setAttribute('aria-haspopup', 'menu');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-label', t('auth.chip.menuAria', { name: opts.displayName }));

  // Меню — выпадающий список СТРОГО под строкой чипа, в ширину рейла (owner-directive
  // 2026-06-12). НЕ модальный rail-popover (тот центрируется оверлеем): лёгкий dropdown,
  // заякоренный к строке — wrap position:relative → menu absolute top:100%, left/right:0
  // (ширина = ширина account-slot = ширина рейла). Закрытие: выбор пункта / клик вне /
  // Esc. Пункты — общий buildMenuBody (Профиль · ——— · Выйти), как было.
  const wrap = document.createElement('div');
  wrap.className = 'account-chip-wrap';

  const menu = document.createElement('div');
  menu.className = 'account-chip-menu';
  menu.setAttribute('role', 'menu');
  menu.hidden = true;
  // Меню = одна accent-пилюля «Выйти» (owner-directive 2026-06-12) — тот же примитив
  // pill-button, что «Войти», но с иконкой выхода. «Профиль» убран (он есть в nav-list).
  // Реактивность: account-chip пере-монтируется целиком из nav-rail.renderAccountSlot()
  // на каждой смене store.locale (applyRailLocale) → t() при сборке = актуальный язык
  // без своей подписки (тот же приём, что login-пилюля гостя в renderAccountSlot).
  const logoutBtn = createPillButton({
    icon: ICON_LOGOUT,
    label: t('auth.chip.logout'),
    className: 'account-chip-logout',
    onClick: () => {
      closeMenu();
      void opts.onLogout();
    },
  }).element;
  menu.append(logoutBtn);

  wrap.append(trigger, menu);
  host.appendChild(wrap);

  let open = false;
  function openMenu(): void {
    if (open) return;
    open = true;
    menu.hidden = false;
    // --open → строка + меню сливаются в ОДНУ плашку (общий surface, плоский шов, CSS).
    wrap.classList.add('account-chip-wrap--open');
    trigger.setAttribute('aria-expanded', 'true');
    // capture-фаза: ловим клик вне ДО того, как он дойдёт до целей внутри рейла.
    document.addEventListener('click', onOutside, true);
    document.addEventListener('keydown', onEsc);
  }
  function closeMenu(): void {
    if (!open) return;
    open = false;
    menu.hidden = true;
    wrap.classList.remove('account-chip-wrap--open');
    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', onOutside, true);
    document.removeEventListener('keydown', onEsc);
  }
  function onOutside(e: MouseEvent): void {
    if (!wrap.contains(e.target as Node)) closeMenu();
  }
  function onEsc(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      closeMenu();
      trigger.focus();
    }
  }
  const onTriggerClick = (e: Event): void => {
    e.stopPropagation();
    if (open) closeMenu();
    else openMenu();
  };
  trigger.addEventListener('click', onTriggerClick);

  return {
    element: wrap,
    unmount(): void {
      closeMenu();
      trigger.removeEventListener('click', onTriggerClick);
      wrap.remove();
    },
  };
}

/** Caret-индикатор раскрываемого меню (Tabler chevron-down). currentColor. */
const ICON_CARET = `<svg class="account-chip-caret-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 9l6 6l6 -6"/></svg>`;

/** Иконка «Выйти» — Tabler logout (дверь + стрелка наружу). currentColor, inline-вектор. */
const ICON_LOGOUT = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"/><path d="M9 12h12l-3 -3"/><path d="M18 15l3 -3"/></svg>`;

/** Маркер «вошёл» — Tabler user-check (человек + галочка). currentColor, inline-вектор. */
const ICON_USER_CHECK = `<svg class="account-chip-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"/><path d="M6 21v-2a4 4 0 0 1 4 -4h4"/><path d="M15 19l2 2l4 -4"/></svg>`;
